// GA4 + Google Ads via gtag.js. `send_page_view` is disabled at config time so
// the app controls page views itself (SPA navigations, see `trackPageView`).
const SCRIPT_ID = 'ga-gtag';

// Google Ads conversion labels carried over from the old dabble-writer app.
// The account id comes from config; these identify the specific conversion
// actions. Verify in Google Ads if attribution ever looks off.
const ADS_SIGNUP_LABEL = 'aoDJCM_5oO0bENeiy4wD';
const ADS_SUBSCRIBE_LABEL = 'AAvSCKSKou0bENeiy4wD';

type GtagArgs = [string, ...unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

// Per Google's enhanced-conversions spec: trim, lowercase, and strip dots /
// plus-tags from gmail so the hashed match key is stable.
function normalizeEmail(email: string): string {
  const normalized = email.trim().toLowerCase();
  const [local, domain] = normalized.split('@');
  if (!domain) return normalized;
  // Only gmail ignores dots and +tags; other providers treat them as significant,
  // so stripping them elsewhere would hash to the wrong address and miss the match.
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    return `${local.split('+')[0].replace(/\./g, '')}@${domain}`;
  }
  return `${local}@${domain}`;
}

// Install the gtag stub + loader script once, and configure GA4 (and Google Ads
// when present). Calls made before the script loads queue on `dataLayer`.
function loadScript(measurementId: string, adsId?: string) {
  if (typeof document === 'undefined' || document.getElementById(SCRIPT_ID)) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.id = SCRIPT_ID;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  if (adsId) window.gtag('config', adsId, { cookie_domain: 'auto' });
  window.gtag('config', measurementId, { send_page_view: false, cookie_domain: 'auto' });
}

/** Load GA4 (+ Google Ads) for the given ids. No-op without a measurement id. */
export function loadGoogleAnalytics(measurementId: string | undefined, adsId?: string) {
  if (!measurementId) return;
  loadScript(measurementId, adsId);
}

/** Attach the signed-in user's id (+ hashed email). No-op until GA has loaded. */
export function identifyGoogleAnalytics(user: { uid: string; email?: string }) {
  if (!window.gtag) return;
  window.gtag('set', { user_id: user.uid });
  if (user.email) window.gtag('set', 'user_data', { email: normalizeEmail(user.email) });
}

/** Emit a GA4 page_view for an SPA navigation. No-op until GA has loaded. */
export function trackGoogleAnalyticsPageView(location: { path: string; title?: string }) {
  if (!window.gtag) return;
  window.gtag('event', 'page_view', {
    page_location: typeof window !== 'undefined' ? window.location.href : location.path,
    page_path: location.path,
    page_title: location.title,
  });
}

/** Fire the registration conversion (GA4 sign_up + Google Ads conversion). */
export function trackGoogleSignup(adsId?: string) {
  if (!window.gtag) return;
  window.gtag('event', 'sign_up', { method: 'password' });
  if (adsId) window.gtag('event', 'conversion', { send_to: `${adsId}/${ADS_SIGNUP_LABEL}` });
}

/** Fire the subscribe conversion (GA4 purchase + subscribe + Google Ads conversion). */
export function trackGoogleSubscribe(
  adsId: string | undefined,
  details: { value: number; currency: string; transactionId?: string; planId?: string; planName?: string },
) {
  if (!window.gtag) return;
  const { value, currency, transactionId, planId, planName } = details;
  // GA4's monetization metrics (Gross purchase revenue etc.) only populate from
  // the reserved ecommerce `purchase` event — the custom `subscribe` event's
  // value never reaches them. `subscribe` stays alongside it: the Ads
  // conversion and existing reports are keyed to it.
  window.gtag('event', 'purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items: [{ item_id: planId, item_name: planName, price: value, quantity: 1 }],
  });
  window.gtag('event', 'subscribe', { value, currency, transaction_id: transactionId });
  if (adsId) {
    window.gtag('event', 'conversion', {
      send_to: `${adsId}/${ADS_SUBSCRIBE_LABEL}`,
      value,
      currency,
      transaction_id: transactionId,
    });
  }
}

/** Clear the associated user id on sign-out. No-op until GA has loaded. */
export function resetGoogleAnalytics() {
  if (!window.gtag) return;
  window.gtag('set', { user_id: null });
}
